/**
 * useActivityMessages — Transient header status for Activity Bar.
 *
 * Phase 7: No longer pushes messages to the chat timeline. Instead,
 * exposes a single `statusMessage` string that the header displays
 * transiently. Each new event replaces the previous status.
 *
 * Recording started/stopped messages removed — activity bar stays open
 * during recording so state is visible directly in the UI.
 *
 * Returns { statusMessage, setStatusMessage, clearStatus }.
 */
import { useState, useEffect, useRef, useCallback } from "react";

export function useActivityMessages({ session, isStreaming }) {
  const [statusMessage, setStatusMessage] = useState("");

  // Prev-state refs for edge detection
  const prevDeviceRef = useRef(session?.selectedDeviceId);

  const push = useCallback((content) => {
    setStatusMessage(content);
  }, []);

  // Mic swap during recording
  useEffect(() => {
    const current = session?.selectedDeviceId;
    const prev = prevDeviceRef.current;
    prevDeviceRef.current = current;
    if (prev && current && prev !== current && isStreaming) {
      push("Microphone switched");
    }
  }, [session?.selectedDeviceId, isStreaming, push]);

  // Phase 12: Processing messages (including errors) moved to NotificationCenter in header.
  // No processing status messages here — AppShell.useProcessingListener handles error notifications.

  const clearStatus = useCallback(() => {
    setStatusMessage("");
  }, []);

  return { statusMessage, setStatusMessage: push, clearStatus };
}
